import React, { useState } from "react";
import { DataUtilities } from "../../../../rule-engine-core/Common/DataUtilities";
import { BaseTestResultData } from "../../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { ConfirmDialog } from "../../../../CommonUI/ConfirmDialog";
import { ApplyIcon } from "../../../../CommonUI/Icon/ApplyIcon";
import { CloseRedIcon } from "../../../../CommonUI/Icon/CloseRedIcon";
import { DeleteGrayIcon } from "../../../../CommonUI/Icon/DeleteGrayIcon";
import { ObserveState } from "../../../../CommonUI/StateManagement/ObserveState";
import { StateManager } from "../../../../rule-engine-core/Common/StateManager";
import { TestContext } from "../../../../CommonUI/Testing/TestContext";

import {
    RuleTableEmptyAddColumn,
    RuleTableHeaderActionColumn,
    RuleTableIndexColumn,
} from "../../../DesignUI/CommonStyles";
import {
    ITableRowTestValue,
    TableRowTestContext,
} from "../../../../rule-engine-core/Rule/Table/Common/TableRowTestContext";
import { PassedTestRow } from "../../CommonStyles";
import { XResultCell } from "../../CommonUI/XResultCell";
import { ValueRuleRow } from "../../../../rule-engine-core/Rule/Table/Value/Structure/ValueRuleRow";
import { ValueTableConfiguration } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableConfiguration";
import { ValueTableRuleEditContext } from "../../../../rule-engine-core/Rule/Table/Value/ValueTableRuleEditContext";
import { XValueTableRuleCell } from "./ValueTableRuleCell";

interface ValueTableRowProps {
    ruleDefinition: ValueTableConfiguration;
    editContext: ValueTableRuleEditContext;
    row: ValueRuleRow;
    index: number;
}

export const XValueTableRow = ({ ruleDefinition, row, index, editContext }: ValueTableRowProps) => {
    const testContext = React.useContext(TestContext);
    const rowtestitem = testContext?.findItem(row._id) as BaseTestResultData;

    let testValue: ITableRowTestValue | undefined;

    const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);

    if (rowtestitem && !rowtestitem.isError) {
        testValue = rowtestitem.result === "false" ? ITableRowTestValue.FAILED : ITableRowTestValue.PASSED;
    }

    return (
        <TableRowTestContext.Provider value={testValue}>
            {rowtestitem?.result === "true" ? (
                <PassedTestRow
                    data-testid={`passedtestrow`}
                    columnsSpan={ruleDefinition.columns.length + ruleDefinition.resultColumns.length + 4}
                    y={index + 3}
                />
            ) : null}
            <RuleTableIndexColumn
                y={index + 3}
                x={0}
                isTestPassed={testValue === ITableRowTestValue.PASSED}
            >
                {rowtestitem?.result === "true" ? <ApplyIcon /> : null}
                {rowtestitem?.result === "false" ? <CloseRedIcon /> : null}
                {index + 1}
            </RuleTableIndexColumn>
            {row.cells.map((cell, index2) => (
                <ObserveState
                    listenTo={cell}
                    properties={["dataType"]}
                    key={index2}
                    control={() => (
                        <XValueTableRuleCell
                            ruleDefinition={ruleDefinition}
                            cell={cell}
                            editContext={editContext}
                            rowIndex={index}
                            cellIndex={index2}
                        />
                    )}
                />
            ))}
            <RuleTableEmptyAddColumn
                isTestPassed={testValue === ITableRowTestValue.PASSED}
                y={index + 3}
                x={ruleDefinition.columns.length + 1}
            />

            {ruleDefinition.resultColumns.map((col, index2) => {
                return (
                    <ObserveState
                        listenTo={col}
                        key={col._id}
                        control={() => (
                            <XResultCell
                                editContext={editContext}
                                resultColumn={col}
                                resultCell={row.resultCellsData[col._id]!}
                                ruleDefinition={ruleDefinition}
                                rowIndex={index + 3}
                                cellIndex={ruleDefinition.columns.length + 2 + index2}
                            />
                        )}
                    />
                );
            })}

            <RuleTableEmptyAddColumn
                y={index + 3}
                x={ruleDefinition.columns.length + ruleDefinition.resultColumns.length + 2}
            />
            <RuleTableHeaderActionColumn
                y={index + 3}
                x={ruleDefinition.columns.length + ruleDefinition.resultColumns.length + 3}
                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                    if (testContext) return null;
                    event.stopPropagation();

                    if (DataUtilities.getCookie("VALUE_TABLE_ROW_DELETE") !== "true") {
                        setOpenConfirmDialog(true);
                    } else {
                        ruleDefinition.removeRow(row);
                        StateManager.propertyChanged(ruleDefinition, "rows");
                    }
                }}
            >
                {testContext ? null : <DeleteGrayIcon />}
            </RuleTableHeaderActionColumn>
            {!testContext && (
                <ConfirmDialog
                    caption="this row"
                    open={openConfirmDialog}
                    onConfirm={(dontAskAgain) => {
                        if (dontAskAgain) {
                            DataUtilities.setCookieValue("VALUE_TABLE_ROW_DELETE", "true");
                        }
                        ruleDefinition.removeRow(row);
                        StateManager.propertyChanged(ruleDefinition, "rows");
                        setOpenConfirmDialog(false);
                    }}
                    onCancel={() => setOpenConfirmDialog(false)}
                />
            )}
        </TableRowTestContext.Provider>
    );
};
